import { Flex } from "@chakra-ui/react";
import { CustomText } from "@root/components/common/components";
import useSidebar from "@root/hooks/useSidebar";
import { NavControl } from "./navControl";
import { SidebarHeader } from "./sidebarHeader";
import { StyledSidebarContainer, StyledSidebarSubBox } from "./style";
import { CustomPopup } from "@root/components/common/modals/CustomPopup";
import { DeletePopupContent } from "@root/pages/components";
import { useEffect, useState } from "react";
import { useGlobalContext } from "@root/context/global-context";
import { LogoutModal } from "@root/components/logout/logout";

export function Sidebar() {
  const {
    isDarkTheme,
    lg,
    sidebar,
    pathname,
    menuItemsList,
    profileData,
    logoutPopup,
    setLogoutPopup,
    deleteAccountPopup,
    setDeleteAccountPopup,
    logoutHandler,
    deleteUserAccount,
    loading,
    t,
    isMenuOpen,
    setIsMenuOpen,
  } = useSidebar();
  const { isSidebarOpen, setIsSidebarOpen } = useGlobalContext();
  const [sidebarState, setSidebarState] = useState<any[]>(sidebar);
  const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null);

  // Function to determine active item based on current pathname
  const getActiveItemKey = (): string | null => {
    // Check if current pathname matches any sidebar item routes
    for (const menu of sidebar) {
      if ((menu as any)?.isHeading) continue;

      // Check if pathname matches any of the routes for this menu item
      const isActive = (menu as any).routes?.some((route: string) => {
        // Handle exact matches
        if (pathname === route) return true;
        // Handle sub-routes (e.g., /risk-assessment/detail/123)
        if (pathname.startsWith(route) && route !== "/") return true;
        return false;
      });

      if (isActive) {
        return (menu as any).key;
      }
    }

    // If no match found, try to get from localStorage
    const savedKey = localStorage.getItem("sidebar-active-key");
    return savedKey;
  };

  // Update active item when pathname changes or on initial load
  useEffect(() => {
    const activeKey = getActiveItemKey();
    if (activeKey) {
      setSelectedItemKey(activeKey);
    }
  }, [pathname, sidebar]);

  useEffect(() => {
    setSidebarState(sidebar);
  }, [sidebar]);

  const SidebarItem = ({ menu }: any) => {
    // If it's a heading, render it differently
    if (menu?.isHeading) {
      return (
        <Flex flexDir={"column"} userSelect={"none"}>
          {isSidebarOpen && (
            <CustomText
              fontSize={"18px"}
              fontWeight={800}
              textColor={"#476370"}
              text={menu.text}
              py={2}
              mt={1}
              mb={1}
              px={"8px"}
              sidebar={true}
            />
          )}
        </Flex>
      );
    }

    // Regular sidebar item rendering (your existing code)
    const isSelected = selectedItemKey === menu.key;
    const iconColor = isSelected ? "#005EB8" : "#5C6670";

    return (
      <Flex flexDir={"column"} userSelect={"none"}>
        <Flex
          alignItems={"center"}
          width={"100%"}
          height={"54px"}
          borderRadius={"8px"}
          justifyContent={isSidebarOpen ? "space-between" : "center"}
          px={"8px"}
          cursor={"pointer"}
          onClick={() => {
            setSelectedItemKey(menu.key);
            // Save to localStorage for persistence across reloads
            localStorage.setItem("sidebar-active-key", menu.key);
            menu.onClick();
          }}
          _hover={{
            bg: "#005EB81F",
          }}
          bg={isSelected ? "#005EB81F" : "transparent"}
        >
          <Flex alignItems={"center"} gap={isSidebarOpen ? "14px" : "0px"}>
            {menu.iconComponent ? (
              <menu.iconComponent
                color={iconColor}
                style={{ color: iconColor }}
                borderRadius="full"
                p="2"
              />
            ) : isSelected ? (
              menu.activeIcon
            ) : (
              menu.icon
            )}
            {isSidebarOpen && (
              <CustomText
                fontSize={"16px"}
                marginRight={"30px"}
                fontWeight={500}
                textColor={iconColor}
                text={menu.text}
                sidebar={true}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    );
  };

  return (
    <StyledSidebarContainer
      padding={isSidebarOpen ? "16px" : "8px"}
      width={isSidebarOpen ? "300px" : "80px"}
      bg={isSidebarOpen ? "bg.box" : "#005EB80F"}
      display="flex"
      flexDirection="column"
      justifyContent="space-between" // pushes NavControl to bottom
      className={"sidebar-container"}
    >
      <SidebarHeader isDarkTheme={isDarkTheme} isOpen={isSidebarOpen} lg={lg} />

      {/* TOP SECTION */}
      <StyledSidebarSubBox>
        <Flex
          mx={"0px"}
          paddingBottom={"32px"}
          flexDirection={"column"}
          gap={"8px"}
          paddingTop={"20px"}
        >
          <Flex flexDir={"column"}>
            {sidebar?.map((menu: any, index: number) => {
              return <SidebarItem key={index} menu={menu} />;
            })}
          </Flex>
        </Flex>
      </StyledSidebarSubBox>

      {/* BOTTOM SECTION */}
      {/* TODO: Remove this once the NavControl is fixed  */}
      {/* <Flex flexDir={"column"} gap={"16px"}>
        <NavControl
          pathname={pathname}
          menuItemsList={menuItemsList}
          isDarkTheme={isDarkTheme}
          isOpen={isSidebarOpen}
          profileData={profileData}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
        />
      </Flex> */}
      <CustomPopup
        open={deleteAccountPopup}
        onClose={() => setDeleteAccountPopup(false)}
        title={t("preferences.signOutModalTitle")}
        children={
          <DeletePopupContent
            loading={loading}
            onDelete={deleteUserAccount}
            onCancel={() => setDeleteAccountPopup(false)}
          />
        }
      />

      <LogoutModal
        isOpen={logoutPopup}
        onClose={() => setLogoutPopup(false)}
        onConfirm={logoutHandler}
      />
    </StyledSidebarContainer>
  );
}
